import { Patient } from '@medplum/fhirtypes'
import { MedplumClient } from '@medplum/core'
import { showNotification } from '@mantine/notifications'
import { createApiClient } from '@behuman.corp/contracts'
import type { ApiClient } from '@behuman.corp/contracts'
import type { OrpcClient } from '@/hooks/useClient'
import { config } from '@/config'
import { logError } from '@/utils/errorUtils'

type EthnicityItem = {
  name: string
  code: string
  system?: string
}
export const LANGUAGE_OPTIONS = [
  {
    code: 'en',
    display: 'English',
  },
  {
    code: 'es',
    display: 'Spanish',
  },
  {
    code: 'other',
    display: 'Other',
  },
]
export const RACE_OPTIONS = [
  {
    name: 'American Indian or Alaska Native',
    code: '1002-5',
    system: 'urn:oid:2.16.840.1.113883.6.238',
  },
  {
    name: 'Asian',
    code: '2028-9',
    system: 'urn:oid:2.16.840.1.113883.6.238',
  },
  {
    name: 'Black or African American',
    code: '2054-5',
    system: 'urn:oid:2.16.840.1.113883.6.238',
  },
  {
    name: 'Native Hawaiian or Other Pacific Islander',
    code: '2076-8',
    system: 'urn:oid:2.16.840.1.113883.6.238',
  },
  {
    name: 'White',
    code: '2106-3',
    system: 'urn:oid:2.16.840.1.113883.6.238',
  },
]

export const ETHNICITY_OPTIONS = [
  {
    group: 'European / Middle Eastern',
    items: [
      { name: 'Ashkenazi Jewish', code: '418034006' },
      { name: 'Sephardic Jewish', code: '448310000' },
      // { "name": "Mizrahi Jewish (Middle-Eastern Jewish populations)", "code": null },
      { name: 'Northern European (e.g., British, Scandinavian)', code: '18126009' },
      { name: 'Southern European (e.g., Italian, Greek, Spanish, Portuguese)', code: '18127000' },
      { name: 'Eastern European (e.g., Polish, Russian, Ukrainian, Baltic)', code: '18128005' },
      { name: 'French Canadian / Acadian', code: '446151000' },
      { name: 'Middle Eastern / Arab (e.g., Lebanese, Syrian, Iranian, Iraqi)', code: '37216000' },
      { name: 'Mediterranean (e.g., Cypriot, Maltese)', code: '448060000' },
      { name: 'Caucasus region (e.g., Georgian, Armenian)', code: '448200000' },
    ],
  },
  {
    group: 'African',
    items: [
      { name: 'West African (e.g., Nigerian, Ghanaian, Sierra Leonean)', code: '315280000' },
      { name: 'East African (e.g., Ethiopian, Somali, Kenyan)', code: '315270000' },
      { name: 'Central African (e.g., Congolese, Ugandan)', code: '315260001' },
      { name: 'Southern African (e.g., South African, Zulu, Xhosa)', code: '315250002' },
      { name: 'North African (e.g., Egyptian, Moroccan, Berber)', code: '315290009' },
      { name: 'African American / Afro-Caribbean', code: '413464008' },
    ],
  },
  {
    group: 'Asian',
    items: [
      { name: 'East Asian (e.g., Chinese, Japanese, Korean)', code: '315300006' },
      {
        name: 'Southeast Asian (e.g., Filipino, Vietnamese, Thai, Indonesian, Cambodian, Malaysian)',
        code: '315310005',
      },
      {
        name: 'South Asian (e.g., Indian, Pakistani, Bangladeshi, Sri Lankan, Nepali)',
        code: '315320003',
      },
      // { "name": "Central Asian (e.g., Uzbek, Kazakh, Tajik)", "code": null }
    ],
  },
  {
    group: 'Pacific Islander / Oceanic',
    items: [
      { name: 'Native Hawaiian', code: '20410005' },
      { name: 'Samoan', code: '37217009' },
      { name: 'Tongan', code: '37218004' },
      { name: 'Guamanian / Chamorro', code: '37220007' },
      // { "name": "Other Polynesian / Micronesian / Melanesian", "code": null }
    ],
  },
  {
    group: 'Indigenous Peoples of the Americas',
    items: [
      { name: 'American Indian (specify tribe)', code: '413497007' },
      { name: 'Alaska Native (specify community)', code: '413498002' },
      { name: 'First Nations (Canada)', code: '418111009' },
      {
        name: 'Central / South American Indigenous (e.g., Quechua, Maya, Aymara)',
        code: '448270006',
      },
    ],
  },
  {
    group: 'Hispanic / Latin American',
    items: [
      { name: 'Hispanic or Latino', code: '2135-2', system: 'urn:oid:2.16.840.1.113883.6.238' },
      { name: 'Puerto Rican', code: '2180-8', system: 'urn:oid:2.16.840.1.113883.6.238' },
      { name: 'Cuban', code: '2148-5', system: 'urn:oid:2.16.840.1.113883.6.238' },
      { name: 'Dominican', code: '2131-1' },
      { name: 'Central American (e.g., Salvadoran, Guatemalan, Costa Rican)', code: '448210005' },
      { name: 'South American (e.g., Colombian, Peruvian, Chilean, Argentine)', code: '448220003' },
      {
        name: 'Other Latino / Latin American',
        code: '2149-3',
        system: 'urn:oid:2.16.840.1.113883.6.238',
      },
    ],
  },
  {
    group: 'Other / Mixed',
    items: [
      { name: 'Other / Mixed Ancestry (specify)', code: '2136-3' },
      {
        name: 'Unknown / Declined to State',
        code: 'UNK',
        display: 'unknown',
        system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
      },
    ],
  },
]
function returnEthnicityOption(code: string): EthnicityItem | undefined {
  for (const g of ETHNICITY_OPTIONS) {
    const found = g.items.find((item) => item.code === code)
    if (found) return found
  }
  return undefined
}

export async function patientExistsByNameDob(
  medplum: MedplumClient,
  params: { family: string; given: string | string[]; birthDate: string },
): Promise<{ exists: boolean; matches: Patient[] | null } | void> {
  const family = params.family.trim()
  const givens = Array.isArray(params.given)
    ? params.given.map((g) => g.trim()).filter(Boolean)
    : params.given
        .split(/\s+/)
        .map((g) => g.trim())
        .filter(Boolean)
  try {
    // Build FHIR search query: family, multiple given tokens, and exact birthDate
    const search = new URLSearchParams()
    search.set('family', family)
    givens.forEach((g) => search.append('given', g))
    search.set('birthdate', params.birthDate) // YYYY-MM-DD
    // Keep it efficient
    search.set('_count', '10')

    const matches = await medplum.searchResources('Patient', search.toString())
    return { exists: matches.length > 0, matches }
  } catch (e) {
    console.log(e)
  }
}

const returnRaceData = (
  raceOption: string,
): { name: string; code: string; system: string } | null => {
  const raceCoddingMap: Record<string, { name: string; code: string; system: string }> = {
    '1002-5': {
      name: 'American Indian or Alaska Native',
      code: '1002-5',
      system: 'urn:oid:2.16.840.1.113883.6.238',
    },
    '2028-9': {
      name: 'Asian',
      code: '2028-9',
      system: 'urn:oid:2.16.840.1.113883.6.238',
    },
    '2054-5': {
      name: 'Black or African American',
      code: '2054-5',
      system: 'urn:oid:2.16.840.1.113883.6.238',
    },
    '2076-8': {
      name: 'Native Hawaiian or Other Pacific Islander',
      code: '2076-8',
      system: 'urn:oid:2.16.840.1.113883.6.238',
    },
    '2106-3': {
      name: 'White',
      code: '2106-3',
      system: 'urn:oid:2.16.840.1.113883.6.238',
    },
  }
  if (raceOption.length === 0 || Object.keys(raceCoddingMap).indexOf(raceOption) === -1) {
    return null
  }

  return raceCoddingMap[raceOption]
}

const addBirthSexExtension = (birthSex: string) => {
  if (birthSex !== 'male' && birthSex !== 'female') {
    return null
  }
  const sexMapping = {
    male: {
      url: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
      valueCode: 'Male',
    },
    female: {
      url: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
      valueCode: 'Female',
    },
  }
  return sexMapping[birthSex]
}
const addEthnicityExtension = (
  primaryEthnicitySet: EthnicityItem[],
  secondaryEthnicitySet: EthnicityItem[],
) => {
  const primaryEthnicityExtensions = primaryEthnicitySet.map((ethnicity) => ({
    url: 'detailed-ethnicity',
    valueCoding: {
      system: ethnicity?.system || 'https://behuman.health/fhir/CodeSystem/behuman-ethnicity',
      code: ethnicity?.code,
      display: ethnicity?.name,
    },
  }))
  const secondaryEthnicityExtensions = secondaryEthnicitySet.map((ethnicity) => ({
    url: 'detailed-second-ethnicity',
    valueCoding: {
      system: ethnicity?.system || 'https://behuman.health/fhir/CodeSystem/behuman-ethnicity',
      code: ethnicity?.code,
      display: ethnicity?.name,
    },
  }))
  return {
    url: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
    extension: [...primaryEthnicityExtensions, ...secondaryEthnicityExtensions],
  }
}
const addMotherMaidenNameExtension = (motherMaidenName: string) => {
  return {
    url: 'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName',
    valueString: motherMaidenName,
  }
}
const addSSNToPatientData = (ssn: string) => {
  return {
    system: 'http://hl7.org/fhir/sid/us-ssn',
    value: ssn.replace(/\D+/g, ''),
  }
}

export type PatientData = {
  addresses: { city: string; state: string; postalCode: string; line: string; use?: string }[]
  firstName: string
  middleName?: string
  lastName: string
  dob: string // YYYY-MM-DD
  gender?: string
  phone?: string
  email?: string
  prefix?: string
  sexAtBirth?: string | null
  race?: string | null
  secondRace?: string | null
  mothersMaidenName?: string | null
  ssn?: string | null
  preferredLanguage?: string | null
  ethnicity: string[]
  secondEthnicity: string[]
}

type Result = { exists: boolean; matches: Patient[] | null } | void

const MANAGED_PATIENT_EXTENSION_URLS = [
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
  'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName',
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
]

function mergeManagedExtensions(
  existingExtensions: Patient['extension'],
  nextManagedExtensions: Patient['extension'],
): Patient['extension'] {
  const preservedExtensions =
    existingExtensions?.filter(
      (extension) => !MANAGED_PATIENT_EXTENSION_URLS.includes(extension.url),
    ) ?? []

  const mergedExtensions = [...preservedExtensions, ...(nextManagedExtensions ?? [])]
  return mergedExtensions.length > 0 ? mergedExtensions : undefined
}

/** `existing` is true when the modal's data matched a chart that already existed: the caller
 *  must not write the typed drafts (coverages) onto that chart. */
export type CreateUpdatePatientResult = { patient: Patient; existing: boolean }

export const createUpdatePatient = async (
  medplum: MedplumClient,
  patientData: PatientData,
  existPatient?: Patient,
  client?: OrpcClient,
): Promise<CreateUpdatePatientResult | undefined> => {
  try {
    const middleware = createApiClient({
      baseUrl: config.middlewareUrl || '-',
      tokenStorage: medplum,
    })
    //check if patient exists
    const formattedDOB = patientData.dob // already in YYYY-MM-DD format

    let result: Result

    const role = await medplum.searchOne(
      'PractitionerRole',
      `practitioner=Practitioner/${(medplum.getProfile() || {})?.id}`,
    )
    if (role && role?.organization?.reference) {
      // PD-1885 — the email check runs first: this practice's chart with the email IS the
      // patient, never a second chart.
      if (!existPatient && client && patientData.email) {
        const { patientId, ambiguous, creating } = await client.patientFindDuplicate({
          email: patientData.email,
          organizationId: role.organization.reference.split('/')[1],
        })
        // PD-1930: another request was just cleared to create this email's chart; creating here
        // too would make a second one.
        if (creating) {
          showNotification({
            title: 'Patient already being created',
            message:
              'A patient with this email is already being created. Check the patient list in a moment.',
            color: 'red',
          })
          return undefined
        }
        if (ambiguous) {
          showNotification({
            title: 'Patient already exists',
            message:
              'A patient with this email already exists. Search the patient list instead of creating a new record.',
            color: 'red',
          })
          return undefined
        }
        if (patientId) {
          showNotification({
            title: 'Patient already exists',
            message: 'A patient with this email already exists. Opening the existing record.',
            color: 'red',
          })
          return { patient: await medplum.readResource('Patient', patientId), existing: true }
        }
      }
      const { exists, matches } = await middleware.post('/patient/search', {
        family: patientData.lastName,
        given: [patientData.firstName, patientData.middleName].filter(
          (name): name is string => typeof name === 'string' && Boolean(name),
        ),
        birthDate: formattedDOB, // Ensure this is in YYYY-MM-DD format,
        organizationId: role.organization.reference.split('/')[1],
      })

      result = { exists, matches: matches as Patient[] | null }
    } else {
      result = await patientExistsByNameDob(medplum, {
        family: patientData.lastName,
        given: [patientData.firstName, patientData.middleName].filter(
          (name): name is string => typeof name === 'string' && Boolean(name),
        ),
        birthDate: formattedDOB, // Ensure this is in YYYY-MM-DD format
      })
    }

    const extension: Patient['extension'] = []
    let identifier: Patient['identifier'] = []
    let communication: Patient['communication'] = []
    if (!existPatient && result && result?.exists && result?.matches && result.matches.length > 0) {
      showNotification({
        title: 'Patient already exists',
        message: `A patient with the name ${patientData.firstName} ${patientData.lastName} and DOB ${patientData.dob} already exists.`,
        color: 'red',
      })
      return { patient: result.matches[0], existing: true }
    }

    const birthDate = formattedDOB
    const allowedGenders = ['male', 'female', 'other', 'unknown'] as const
    const gender = allowedGenders.includes(patientData.gender as (typeof allowedGenders)[number])
      ? (patientData.gender as (typeof allowedGenders)[number])
      : undefined
    if (patientData?.sexAtBirth) {
      const birthSexExt = addBirthSexExtension(patientData.sexAtBirth)
      if (birthSexExt) {
        extension.push(birthSexExt)
      }
    }
    if (patientData?.mothersMaidenName) {
      extension.push(addMotherMaidenNameExtension(patientData.mothersMaidenName))
    }
    if (patientData?.ssn) {
      identifier = [addSSNToPatientData(patientData.ssn)]
    }
    if (patientData?.ethnicity.length > 0 || patientData?.secondEthnicity.length > 0) {
      const primaryEthnicitySet: EthnicityItem[] =
        patientData?.ethnicity && patientData?.ethnicity.length > 0
          ? patientData.ethnicity
              .map((eth) => returnEthnicityOption(eth))
              .filter((e): e is EthnicityItem => !!e)
          : []
      const secondaryEthnicitySet: EthnicityItem[] =
        patientData?.secondEthnicity && patientData?.secondEthnicity.length > 0
          ? patientData.secondEthnicity
              .map((eth) => returnEthnicityOption(eth))
              .filter((e): e is EthnicityItem => !!e)
          : []
      const extensionData = addEthnicityExtension(primaryEthnicitySet, secondaryEthnicitySet)
      extension.push(extensionData)
    }
    if (patientData?.race || patientData?.secondRace) {
      const raceExtension = extension.find(
        (ext) => ext?.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
      )
      let [primaryRaceExt, secondaryRaceExt] = raceExtension?.extension
        ? raceExtension.extension
        : [{}, {}]
      if (patientData?.race) {
        const raceData = returnRaceData(patientData.race)
        primaryRaceExt = raceData
          ? {
              url: 'ombCategory',
              valueCoding: {
                system: raceData.system,
                code: raceData.code,
                display: raceData.name,
              },
            }
          : {}
      }
      if (patientData?.secondRace) {
        const raceData = returnRaceData(patientData.secondRace)
        secondaryRaceExt = raceData
          ? {
              url: 'ombCategory',
              valueCoding: {
                system: raceData.system,
                code: raceData.code,
                display: raceData.name,
              },
            }
          : {}
      }
      const updatedRaceExtension = {
        url: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
        extension: [primaryRaceExt, secondaryRaceExt].filter(
          (ext) => Object.keys(ext).length > 0,
        ) as {
          url: string
          valueCoding: { system: string; code: string; display: string }
        }[],
      }
      extension.push(updatedRaceExtension)
    }
    if (patientData?.preferredLanguage) {
      const lang = JSON.parse(patientData.preferredLanguage)
      if (lang && lang.code) {
        communication = [
          {
            language: {
              coding: [
                {
                  system: 'urn:ietf:bcp:47',
                  code: lang.code,
                  display: lang.display,
                },
              ],
              text: lang.text,
            },
            preferred: true,
          },
        ]
      }
    }
    // Process addresses array - filter out empty addresses and format properly
    const address = patientData.addresses
      .filter(
        (addr) =>
          addr.use === 'old' ||
          addr.line.trim().length > 0 ||
          addr.city.trim().length > 0 ||
          addr.state.trim().length > 0 ||
          addr.postalCode.trim().length > 0,
      )
      .map((addr) => {
        const line = addr.line.trim()
        const city = addr.city.trim()
        const state = addr.state.trim()
        const postalCode = addr.postalCode.trim()
        return {
          ...(line ? { line: [line] } : {}),
          ...(city ? { city } : {}),
          ...(state ? { state } : {}),
          ...(postalCode ? { postalCode } : {}),
          country: 'US',
          ...(addr.use ? { use: addr.use as 'old' } : {}),
        }
      })

    // Ensure at least one address exists if any address data was provided
    if (address.length === 0 && patientData.addresses.length > 0) {
      const firstAddr = patientData.addresses[0]
      const line = firstAddr.line.trim()
      const city = firstAddr.city.trim()
      const state = firstAddr.state.trim()
      const postalCode = firstAddr.postalCode.trim()
      if (line || city || state || postalCode) {
        address.push({
          ...(line ? { line: [line] } : {}),
          ...(city ? { city } : {}),
          ...(state ? { state } : {}),
          ...(postalCode ? { postalCode } : {}),
          country: 'US',
        })
      }
    }
    const telecom = [
      { system: 'phone' as const, value: patientData?.phone || '', use: 'temp' as const },
      { system: 'email' as const, value: patientData.email },
    ]
    const mergedExtension = mergeManagedExtensions(existPatient?.extension, extension)

    let payload: Patient = {
      resourceType: 'Patient',
      birthDate,
      gender,
      address,
      telecom,
      communication,
      extension: mergedExtension,
      identifier,
      name: [
        {
          family: patientData.lastName,
          given: [patientData.firstName, patientData.middleName].filter(
            (name): name is string => typeof name === 'string' && Boolean(name),
          ),
          prefix: patientData.prefix ? [patientData.prefix] : undefined,
        },
      ],
    }

    let finalPatient: Patient
    if (existPatient) {
      payload = {
        ...existPatient,
        ...payload,
      }

      finalPatient = await medplum.updateResource(payload)
    } else {
      finalPatient = await medplum.createResource(payload)
      // PD-1993: a patient staff add by hand is a CRM lead with source Manual / Import. Right
      // after the chart exists, ahead of the org bot, so a failing bot can't lose the lead;
      // best-effort, never blocking the patient.
      if (client && finalPatient.id) {
        client.crmLeadRecordManual({ patientId: finalPatient.id }).catch((error: unknown) => {
          logError('patient-modal', 'Recording the CRM lead failed', error)
        })
      }
      if (role?.organization?.reference) {
        const bot = await medplum.searchOne('Bot', 'name=org-patient-handshake')
        if (bot) {
          await medplum.executeBot(bot.id, {
            patientId: finalPatient.id,
            organizationId: role.organization.reference.split('/')[1],
          })
        }
      }
      // Patient created successfully
      const sendInvite = async () => {
        try {
          await middleware.post('/patient/invite', {
            resource: finalPatient,
          })
        } catch (error) {
          logError('patient-modal', 'Resend invitation failed', error)
        }
      }
      void sendInvite()

      showNotification({
        title: 'Patient created',
        message: `A new patient has been created.`,
        color: 'green',
      })
    }

    try {
      await syncPatientWithBrevo(
        middleware,
        patientData,
        formattedDOB,
        existPatient ? 'update' : 'create',
      )
    } catch (brevoError) {
      logError('patient-modal', 'Brevo sync failed', brevoError)
    }
    return { patient: finalPatient, existing: Boolean(existPatient) }
  } catch (error) {
    logError('patient-modal', 'Error creating patient', error)
    showNotification({
      title: 'Error',
      message: 'An error occurred while creating the patient.',
      color: 'red',
    })
  }
}

export async function syncPatientWithBrevo(
  middleware: ApiClient,
  patientData: PatientData,
  formattedDOB: string,
  operation: 'create' | 'update',
) {
  if (!patientData.email) {
    return
  }

  // PD-1425: index 0 is frequently a soft-deleted address — the Patient App keeps a
  // removed address as `use: 'old'` — which sent Brevo a blank address on every
  // update. Same `.find((a) => a.use !== 'old')` idiom as patient-web's
  // syncProfileWithBrevo and the other address readers across the repo.
  const primaryAddress = patientData.addresses?.find((addr) => addr.use !== 'old')

  await middleware.post('/patient/brevo-sync', {
    operation,
    email: patientData.email,
    firstName: patientData.firstName,
    lastName: patientData.lastName,
    dateOfBirth: formattedDOB,
    gender: patientData.gender,
    address: primaryAddress?.line || '',
    city: primaryAddress?.city || '',
    state: primaryAddress?.state || '',
    zipCode: primaryAddress?.postalCode || '',
    phone: patientData?.phone || '',
  })
}

function getSSNFromPatient(patient: Patient): string | null {
  const ssnIdentifier = patient?.identifier?.find(
    (identifier) => identifier.system === 'http://hl7.org/fhir/sid/us-ssn',
  )
  return ssnIdentifier?.value || null
}
function getMothersMaidenNameFromPatient(patient: Patient): string | null {
  const motherMaidenNameExtension = patient?.extension?.find(
    (ext) => ext.url === 'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName',
  )
  return motherMaidenNameExtension?.valueString || null
}
function getBirthSexFromPatient(patient: Patient): string | null {
  const birthSexExtension = patient?.extension?.find(
    (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
  )
  return birthSexExtension?.valueCode?.toLowerCase() || null
}
function getPreferredLanguageFromPatient(patient: Patient): string | null {
  const preferredLanguage = patient?.communication?.find((comm) => comm.preferred === true)
  if (
    preferredLanguage &&
    preferredLanguage.language &&
    preferredLanguage.language.coding &&
    preferredLanguage.language.coding.length > 0
  ) {
    return JSON.stringify({
      code: preferredLanguage.language.coding[0].code,
      display: preferredLanguage.language.coding[0].display,
      text: preferredLanguage.language.text,
    })
  }
  return null
}
function getRaceFromPatient(patient: Patient): string[] | null[] {
  const raceExtension = patient?.extension?.find(
    (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
  )
  const ombCategory = raceExtension?.extension?.filter((ext) => ext.url === 'ombCategory')
  if (!ombCategory || ombCategory.length === 0) {
    return []
  }
  return ombCategory
    .map((option) => option?.valueCoding?.code || null)
    .filter((code): code is string => code !== null)
}

function getEthnicityFromPatient(patient: Patient): string[] {
  const ethnicityExtension = patient?.extension?.find(
    (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
  )
  const primaryEthnicityExtensions = ethnicityExtension?.extension?.filter(
    (ext) => ext.url === 'detailed-ethnicity',
  )
  if (!primaryEthnicityExtensions || primaryEthnicityExtensions.length === 0) {
    return []
  }
  return primaryEthnicityExtensions
    .map((option) => option?.valueCoding?.code || null)
    .filter((code): code is string => code !== null)
}
function getSecondEthnicityFromPatient(patient: Patient): string[] {
  const ethnicityExtension = patient?.extension?.find(
    (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
  )
  const secondaryEthnicityExtensions = ethnicityExtension?.extension?.filter(
    (ext) => ext.url === 'detailed-second-ethnicity',
  )
  if (!secondaryEthnicityExtensions || secondaryEthnicityExtensions.length === 0) {
    return []
  }
  return secondaryEthnicityExtensions
    .map((option) => option?.valueCoding?.code || null)
    .filter((code): code is string => code !== null)
}

export {
  getRaceFromPatient,
  getSSNFromPatient,
  getMothersMaidenNameFromPatient,
  getBirthSexFromPatient,
  getPreferredLanguageFromPatient,
  getEthnicityFromPatient,
  getSecondEthnicityFromPatient,
  returnEthnicityOption,
}
